import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// User interface matching Firebase User properties
export interface User {
   uid: string;
   email: string | null;
   displayName: string | null;
   photoURL: string | null;
   emailVerified: boolean;
   phoneNumber: string | null;
   // Additional custom properties
   fullName?: string;
   accountType?: string;
   createdAt?: string; // Changed to string for serialization
   updatedAt?: string; // Changed to string for serialization
   // Location information
   location?: {
      latitude: number;
      longitude: number;
      address: string;
      city?: string;
      country?: string;
      placeId?: string;
   } | null;
   subscription?: {
      active: boolean;
      status: string;
      plan: string;
      interval: string;
      subscriptionId: string;
      userId: string;
      ended_at: string; // Changed to string for serialization
      started_at: string; // Changed to string for serialization
   } | null;
   hasActiveSubscription?: boolean;
}

interface UserState {
   currentUser: User | null;
   isAuthenticated: boolean;
   loading: boolean;
   error: string | null;
   sessionId: string | null; // Add session management to Redux
}

const initialState: UserState = {
   currentUser: null,
   isAuthenticated: false,
   loading: true, // Start with loading true to check authentication
   error: null,
   sessionId: null,
};

const userSlice = createSlice({
   name: 'user',
   initialState,
   reducers: {
      // Set loading state
      setLoading: (state, action: PayloadAction<boolean>) => {
         state.loading = action.payload;
      },

      // Login user
      loginUser: (state, action: PayloadAction<User>) => {
         state.currentUser = action.payload;
         state.isAuthenticated = true;
         state.loading = false;
         state.error = null;
         // Generate new session ID on login
         state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      },

      // Update user data
      updateUser: (state, action: PayloadAction<Partial<User>>) => {
         if (state.currentUser) {
            state.currentUser = { ...state.currentUser, ...action.payload };
         }
      },

      // Logout user
      logoutUser: (state) => {
         state.currentUser = null;
         state.isAuthenticated = false;
         state.loading = false;
         state.error = null;
         state.sessionId = null; // Clear session on logout
      },

      // Set error
      setError: (state, action: PayloadAction<string | null>) => {
         state.error = action.payload;
         state.loading = false;
      },

      // Clear error
      clearError: (state) => {
         state.error = null;
      },

      // Set authentication checking complete (for initial load)
      setAuthChecked: (state) => {
         state.loading = false;
      },

      // Set or generate session ID
      setSessionId: (state, action: PayloadAction<string | null>) => {
         if (action.payload) {
            state.sessionId = action.payload;
         } else {
            // Generate new session ID
            state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
         }
      },

      // Generate new session ID
      generateNewSession: (state) => {
         state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      },
   },
});

// Export actions
export const {
   setLoading,
   loginUser,
   updateUser,
   logoutUser,
   setError,
   clearError,
   setAuthChecked,
   setSessionId,
   generateNewSession,
} = userSlice.actions;

// Export selectors
export const selectCurrentUser = (state: { user: UserState }) => state.user.currentUser;
export const selectIsAuthenticated = (state: { user: UserState }) => state.user.isAuthenticated;
export const selectUserLoading = (state: { user: UserState }) => state.user.loading;
export const selectUserError = (state: { user: UserState }) => state.user.error;
export const selectSessionId = (state: { user: UserState }) => state.user.sessionId;

// Export reducer
export default userSlice.reducer;
